import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export data to CSV
 * @param {Array} headers - Array of header strings
 * @param {Array} rows - Array of arrays (each representing a row)
 * @param {string} filename - Filename to save as (without extension)
 */
export const exportToCSV = (headers, rows, filename = 'export') => {
  const content = [
    headers.join(','),
    ...rows.map(row => 
      row.map(cell => {
        const cellValue = cell === null || cell === undefined ? '' : String(cell);
        // Escape quotes and wrap in quotes if contains comma, newline or quotes
        if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
          return `"${cellValue.replace(/"/g, '""')}"`;
        }
        return cellValue;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data to Excel (HTML format that Excel can open)
 * @param {Array} headers - Array of header strings
 * @param {Array} rows - Array of arrays
 * @param {string} filename - Filename to save as
 */
export const exportToExcel = (headers, rows, filename = 'export') => {
  const tableHtml = `
    <table>
      <thead>
        <tr>${headers.map(h => `<th style="background-color: #4CAF50; color: white; border: 1px solid #ddd; padding: 8px;">${h}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${rows.map(row => `
          <tr>${row.map(cell => `<td style="border: 1px solid #ddd; padding: 8px;">${cell === null || cell === undefined ? '' : cell}</td>`).join('')}</tr>
        `).join('')}
      </tbody>
    </table>
  `;

  const template = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Export</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
      <body>${tableHtml}</body>
    </html>
  `;

  const blob = new Blob([template], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.xls`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data to PDF
 * @param {string} title - Title of the PDF
 * @param {Array} headers - Array of header strings
 * @param {Array} rows - Array of arrays
 * @param {string} filename - Filename to save as
 */
export const exportToPDF = (title, headers, rows, filename = 'export') => {
  const doc = new jsPDF('p', 'pt', 'a4');
  
  // Add title
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  doc.text(title, 40, 40);
  
  // Add timestamp
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 40, 60);

  // Generate table
  autoTable(doc, {
    startY: 80,
    head: [headers],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    styles: { fontSize: 8, cellPadding: 3 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 40, right: 40 }
  });

  doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Print data
 * @param {string} title
 * @param {Array} headers 
 * @param {Array} rows
 */
export const printPage = (title, headers, rows) => {
  if (!headers || !rows) {
    window.print();
    return;
  }
  
  const tableHtml = `
    <h2 style="font-family: sans-serif; margin-bottom: 20px; color: #333;">${title}</h2>
    <table style="width: 100%; border-collapse: collapse; font-family: sans-serif; font-size: 13px;">
      <thead>
        <tr>${headers.map(h => `<th style="background-color: #f8fafc; color: #333; border: 1px solid #e2e8f0; padding: 10px; text-align: left;">${h}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${rows.map(row => `
          <tr>${row.map(cell => `<td style="border: 1px solid #e2e8f0; padding: 10px; color: #333;">${cell === null || cell === undefined ? '' : cell}</td>`).join('')}</tr>
        `).join('')}
      </tbody>
    </table>
  `;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups for printing.');
    return;
  }
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print - ${title}</title>
      </head>
      <body style="padding: 20px; margin: 0;" onload="setTimeout(() => { window.print(); window.close(); }, 250);">
        ${tableHtml}
      </body>
    </html>
  `);
  printWindow.document.close();
};

export default {
  exportToCSV,
  exportToExcel,
  exportToPDF,
  printPage
};
